import React, { useRef, useEffect, useState, useCallback } from "react";
import {  LuUndo, LuX } from "react-icons/lu";
import { CiLock, CiUnlock } from "react-icons/ci";
// import html2canvas from "html2canvas";
import socket from "./socket";

const DrawingOverlay = ({ onClose }) => {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [strokes, setStrokes] = useState([]);
  const [currentStroke, setCurrentStroke] = useState([]);
  const [penColor, setPenColor] = useState("yellow");
  const [isLocked, setIsLocked] = useState(false); 

  const colors = ["yellow", "red", "white", "green"];

  const getXY = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) / rect.width,
      y: (e.clientY - rect.top) / rect.height,
    };
  };

  const getTouchXY = (touch) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return {
      x: (touch.clientX - rect.left) / rect.width,
      y: (touch.clientY - rect.top) / rect.height,
    };
  };

  const startDrawing = (e) => {
    setDrawing(true);
    const stroke = [getXY(e)];
    setCurrentStroke(stroke);
  };

  const startTouchDrawing = (e) => {
    if (!isLocked && e.touches.length > 1) return;
    if (e.touches.length !== 1) return;
    e.preventDefault();
    setDrawing(true);
    const stroke = [getTouchXY(e.touches[0])];
    setCurrentStroke(stroke);
  };

  const draw = (e) => {
    if (!drawing) return;
    const newPoint = getXY(e);
    drawAndEmit(newPoint);
  };

  const touchDraw = (e) => {
    if (!drawing || e.touches.length !== 1) return;
    e.preventDefault();
    const newPoint = getTouchXY(e.touches[0]);
    drawAndEmit(newPoint);
  };

  const drawAndEmit = (newPoint) => {
    setCurrentStroke((prev) => [...prev, newPoint]);
    const prevPoint = currentStroke[currentStroke.length - 1];
    const ctx = canvasRef.current.getContext("2d");
    const canvas = canvasRef.current;

    if (prevPoint) {
      ctx.strokeStyle = penColor;
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(prevPoint.x * canvas.width, prevPoint.y * canvas.height);
      ctx.lineTo(newPoint.x * canvas.width, newPoint.y * canvas.height);
      ctx.stroke();

      socket.emit("draw", {
        from: prevPoint,
        to: newPoint,
        color: penColor,
      });
    }
  };

  const stopDrawing = () => {
    if (currentStroke.length > 0) {
      setStrokes((prev) => [
        ...prev,
        { points: currentStroke, color: penColor },
      ]);
    }
    setDrawing(false);
    setCurrentStroke([]);
  };

  const drawRemoteStroke = ({ from, to, color }) => {
    const ctx = canvasRef.current.getContext("2d");
    const canvas = canvasRef.current;
    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(from.x * canvas.width, from.y * canvas.height);
    ctx.lineTo(to.x * canvas.width, to.y * canvas.height);
    ctx.stroke();
  };

  const redrawStrokes = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    strokes.forEach((stroke) => {
      ctx.strokeStyle = stroke.color;
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.beginPath();
      stroke.points.forEach((point, index) => {
        const x = point.x * canvas.width;
        const y = point.y * canvas.height;
        if (index === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      });
      ctx.stroke();
    });
  }, [strokes]);

  useEffect(() => {
    const canvas = canvasRef.current;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      redrawStrokes();
    };

    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);

    socket.on("draw", drawRemoteStroke);
    socket.on("clearCanvas", () => setStrokes([]));

    return () => {
      window.removeEventListener("resize", resizeCanvas);
      socket.off("draw", drawRemoteStroke);
      socket.off("clearCanvas");
    };
  }, [redrawStrokes]);

  const handleUndo = () => {
    setStrokes((prev) => prev.slice(0, -1));
  };

  // const handleSave = () => {
  //   html2canvas(document.body, {
  //     useCORS: true,
  //     allowTaint: true,
  //     backgroundColor: null,
  //   }).then((canvas) => {
  //     const link = document.createElement("a");
  //     link.download = "screenshot.png";
  //     link.href = canvas.toDataURL("image/png");
  //     link.click();
  //   });
  // };

  const iconButtonStyle = {
    padding: "10px",
    background: "#fff",
    border: "1px solid #ccc",
    borderRadius: "50%",
    cursor: "pointer",
    boxShadow:
      "inset 0 2px 6px rgba(0, 0, 0, 0.5), inset 0 -2px 4px rgba(255, 255, 255, 0.05)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  return (
    <>
      <canvas
        ref={canvasRef}
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
        onTouchStart={startTouchDrawing}
        onTouchMove={touchDraw}
        onTouchEnd={stopDrawing}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          zIndex: 9999,
          backgroundColor: "transparent",
          pointerEvents: "auto",
          touchAction: isLocked ? "none" : "manipulation",
        }}
      />

      <div
        style={{
          position: "fixed",
          bottom: 20,
          right: 20,
          display: "flex",
          flexDirection: "row",
          gap: 10,
          zIndex: 10000,
        }}
      >
        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
          {colors.map((color) => (
            <div
              key={color}
              onClick={() => setPenColor(color)}
              style={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                backgroundColor: color,
                border:
                  penColor === color ? "3px solid #fff" : "2px solid #ccc",
                cursor: "pointer",
              }}
            />
          ))}
        </div>

        <button onClick={handleUndo} style={iconButtonStyle} title="Undo">
          <LuUndo size={18} />
        </button>
        {/* <button onClick={handleSave} style={iconButtonStyle} title="Save">
          <LuArrowDownToLine size={18} />
        </button> */}
        <button onClick={onClose} style={iconButtonStyle} title="Close">
          <LuX size={18} />
        </button>
      </div>

      <div
        style={{
          position: "fixed",
          bottom: 20,
          left: 20,
          zIndex: 10000,
        }}
      >
        <button
          onClick={() => setIsLocked(!isLocked)}
          style={{
            ...iconButtonStyle,
            backgroundColor: isLocked ? "#e74c3c" : "#2ecc71", 
            color: "#fff",
          }}
          title={isLocked ? "Locked: No Scroll" : "Unlocked: Scroll Enabled"}
        >
          {isLocked ? <CiLock size={18} /> : <CiUnlock size={18} />}
        </button>
      </div>
    </>
  );
};

export default DrawingOverlay;
